import React, { useCallback, useMemo } from 'react'
import {
  AnyActionType,
  SetStateAction,
  ISetStateAction,
  IActionComponentProps,
} from '~core/forge.type'
import useDispatch from '~editor/hooks/useDispatch'
import { Box, HStack } from '@chakra-ui/react'
import { Select, MultiSelect } from '~editor/components/Select'
import { useSelector } from 'react-redux'
import {
  getNodesWithoutInternals,
  getSelectedNode,
} from '~editor/selectors/nodes'
import { useComponent, library } from '~core/componentLibrary'

interface IProps extends IActionComponentProps {
  action: SetStateAction
}

interface IOption {
  label: string
  value: SetStateAction['option']
}

export const SetStateActionComponent = ({
  action,
  actions,
  activeActionGesture,
  isDisabled,
}: IProps) => {
  const dispatch = useDispatch()
  const node = useSelector(getSelectedNode)
  const component = useComponent(node.type)
  const nodesWithouthInternals = useSelector(getNodesWithoutInternals)
  const nodesOptions = useMemo(() => {
    return Object.values(nodesWithouthInternals)
      .filter((_node) => {
        let hasSameStates = true
        const _component = library.get(_node.type)
        const _statesKeys = Object.keys(_component.forge.states)
        const statesKeys = Object.keys(component.forge.states)

        for (const key of statesKeys) {
          if (!_statesKeys.includes(key)) {
            hasSameStates = false
            break
          }
        }

        return hasSameStates
      })
      .map((_node) => {
        let label = _node.name || _node.type

        if (_node.id === node.id) {
          label = `[Current] ${label}`
        }

        return {
          label,
          value: _node.id,
        }
      })
  }, [nodesWithouthInternals])

  const nodesIDs = useMemo(() => {
    if (!action.nodeIDs) return []
    return action.nodeIDs
      .map((nodeID) => {
        return nodesOptions.find((nodeOption) => {
          return nodeOption.value === nodeID
        })
      })
      .filter((nodeOption) => nodeOption)
  }, [action.nodeIDs, nodesOptions])

  const options = useMemo(() => {
    const _options: IOption[] = []

    if (component.forge.states) {
      if (Object.entries(component.forge.states).length === 2) {
        _options.push({ label: 'Toggle', value: 'toggle' })
      }

      for (const [key, value] of Object.entries(component.forge.states)) {
        _options.push({ label: value.label, value: key })
      }
    }

    return _options
  }, [component])

  const optionValue = useMemo(() => {
    return (
      options.find((option) => action.option === option.value) || options[0]
    )
  }, [action.option, options])

  const updateNodeIDs = useCallback(
    (values: IOption[]) => {
      const _nodeIDs = values.map(({ value }) => value)
      dispatch.nodes.updateActions({
        actionGestureType: activeActionGesture,
        actions: actions.map((_a: AnyActionType) => {
          const _action = { ..._a } as SetStateAction

          if (action.id === _action.id) {
            _action.nodeIDs = _nodeIDs
          }

          return _action
        }),
      })
    },
    [dispatch, activeActionGesture, actions],
  )

  const addNodeIDs = useCallback(
    (values: IOption[]) => {
      const filteredNodes = values.filter(({ value }) => {
        return !action.nodeIDs.includes(value)
      })

      updateNodeIDs([
        ...action.nodeIDs.map((nodeID) => ({
          label: nodeID,
          value: nodeID,
        })),
        ...filteredNodes,
      ])
    },
    [action.nodeIDs, updateNodeIDs],
  )

  const removeNodeIDs = useCallback(
    (values: IOption[]) => {
      const filteredNodes = action.nodeIDs
        .filter((nodeID) => {
          return !values.find(({ value }) => value === nodeID)
        })
        .map((nodeID) => ({
          label: nodeID,
          value: nodeID,
        }))

      updateNodeIDs(filteredNodes)
    },
    [action.nodeIDs, updateNodeIDs],
  )

  const updateAction = useCallback(
    (value: ISetStateAction['option']) => {
      dispatch.nodes.updateActions({
        actionGestureType: activeActionGesture,
        actions: actions.map((_a: AnyActionType) => {
          const _action = { ..._a } as SetStateAction
          if (action.id === _action.id) {
            _action.option = value
          }

          return _action
        }),
      })
    },
    [dispatch, activeActionGesture, actions],
  )

  return (
    <Box width="100%" display="flex" alignItems="center">
      <HStack spacing="24px" flex="1">
        <MultiSelect
          value={nodesIDs as any}
          width="auto"
          options={nodesOptions}
          onChange={updateNodeIDs as any}
          selectAll={addNodeIDs}
          deselectAll={removeNodeIDs}
        />
        <Select
          value={optionValue}
          width="150px"
          options={options}
          isDisabled={isDisabled}
          onChange={(option) => {
            // @ts-ignore
            updateAction(option.value)
          }}
        />
        {/* <Button
          variant="warningGhost"
          fontSize="12px"
          onClick={() => {
            updateNodeIDs([])
          }}
        >
          {'Clear'}
        </Button> */}
      </HStack>
    </Box>
  )
}

export default SetStateActionComponent
